import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal, WritableSignal } from '@angular/core';
import { ChatSession } from '@local/client-contracts/src/chats';
import { ContextMenuItem, ContextMenuService } from '@shared/components';
import { take, takeWhile, interval } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InvokeCommand } from 'src/app/bar/views/results/models/invoke-command.model';
import { ChatsService } from '../../../services/chats.service';
import { ChatsPopupService } from '../../../services/chats-popup.service';
@UntilDestroy()
@Component({
  selector: 'chat-sidebar-session-item',
  templateUrl: './chat-sidebar-session-item.component.html',
  styleUrls: ['./chat-sidebar-session-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatSidebarSessionItem {
  private contextMenuService: ContextMenuService = inject(ContextMenuService);
  private activeContextMenu;
  private chatService: ChatsService = inject(ChatsService);
  private chatsPopupService: ChatsPopupService = inject(ChatsPopupService);

  title: string;

  isOpenContextMenu: WritableSignal<boolean> = signal(false);
  tooltipDisabled: WritableSignal<boolean> = signal(true);
  isRenameState: WritableSignal<boolean> = signal(false);
  localName: WritableSignal<string> = signal('');
  generateName: WritableSignal<string> = signal('');

  isSelected = computed(() => this.sessionItem()?.id === this.selectedSessionId());
  displayName = computed(() => {
    return this.localName() || this.sessionItem()?.name || 'New Chat';
  });

  sessionItem = input<ChatSession>();
  selectedSessionId = input<string>();

  currentTypingSessionId: WritableSignal<string> = model(null);

  constructor() {
    effect(
      () => {
        if (this.currentTypingSessionId() === this.sessionItem()?.id) {
          this.currentTypingSessionId.set(null);
          let index = 1;
          this.generateName.set(this.sessionItem().name.slice(0, index));
          interval(50)
            .pipe(
              untilDestroyed(this),
              takeWhile(() => index < this.sessionItem().name.length)
            )
            .subscribe(() => {
              this.generateName.set(this.sessionItem().name.slice(0, index));
              index++;
              if (index === this.sessionItem().name.length) {
                this.generateName.set(null);
              }
            });
        }
      },
      { allowSignalWrites: true }
    );
  }

  openContextMenu(event: { originalEvent: any; checked: boolean }) {
    if (!event.checked) return;
    event.originalEvent.stopPropagation();
    const openAt: { x: number; y: number } = {
      x: event.originalEvent?.x,
      y: event.originalEvent?.y,
    };
    const items = this.buildContextMenuItems();
    this.activeContextMenu = this.contextMenuService.open(
      openAt,
      {
        items,
        onInvoke: (item: InvokeCommand) => this.handleContextMenuCommand(item),
      },
      { position: 'right' }
    );
    this.isOpenContextMenu.set(true);
    this.activeContextMenu.destroy$.pipe(take(1)).subscribe(() => {
      this.isOpenContextMenu.set(false);
    });
  }

  private async handleContextMenuCommand(command: ContextMenuItem) {
    if (command.id === 'rename') {
      this.isRenameState.set(true);
    } else if (command.id === 'delete') {
      this.chatsPopupService.deleteSessionPopup(this.sessionItem()?.id, this.sessionItem()?.assistantId, this.isSelected());
    }
  }

  private buildContextMenuItems(): ContextMenuItem[] {
    return [
      {
        id: 'rename',
        text: 'Rename',
        icon: { type: 'font-icon', value: 'icon-edit-frame' },
      },
      {
        id: 'delete',
        text: 'Delete',
        icon: { type: 'font-icon', value: 'icon-delete' },
      },
    ];
  }

  isTextTruncated(element: HTMLElement): boolean {
    return element.offsetWidth < element.scrollWidth;
  }

  updateTooltip(element: HTMLElement) {
    this.tooltipDisabled.set(!(element.offsetWidth < element.scrollWidth));
  }

  onChange($event) {
    this.title = $event;
  }

  updateName() {
    this.isRenameState.set(false);
    const title = this.title?.trim();
    if (this.sessionItem()?.name !== this.title && title) {
      this.localName.set(title);
      this.chatService.updateSession({ chatId: this.sessionItem()?.id, newName: title });
    }
  }
}
